import { useSyncExternalStore } from 'react'

/** A logger the app has connected to, kept so it can be reconnected to and named. */
export interface KnownDevice {
  /** The Wi-Fi network name when known (it carries the serial), else the logger's own name */
  id: string
  /** The name the logger reports over the protocol */
  name: string
  ssid?: string
  /** What the user calls it */
  nick?: string
  /** The user was offered a nickname and skipped it: do not ask again */
  nickAsked?: boolean
  lastSeen: string
}

const KEY = 'mala.devices'
let devices: KnownDevice[] = load()
const listeners = new Set<() => void>()

function load(): KnownDevice[] {
  try {
    const raw = localStorage.getItem(KEY)
    const parsed = raw ? (JSON.parse(raw) as KnownDevice[]) : []
    return Array.isArray(parsed) ? parsed.filter((d) => d && typeof d.id === 'string') : []
  } catch {
    return []
  }
}

function commit(next: KnownDevice[]) {
  devices = next
  try {
    localStorage.setItem(KEY, JSON.stringify(next))
  } catch {
    /* storage may be unavailable */
  }
  for (const f of listeners) f()
}

export function getDevices(): KnownDevice[] {
  return devices
}

export function subscribeDevices(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

export function useDevices(): KnownDevice[] {
  return useSyncExternalStore(subscribeDevices, getDevices)
}

/** Add or update a device; most recently seen first. */
export function upsertDevice(patch: Partial<KnownDevice> & { id: string }): KnownDevice {
  const cur = devices.find((d) => d.id === patch.id)
  const next: KnownDevice = { name: patch.id, lastSeen: new Date().toISOString(), ...cur, ...patch }
  commit([next, ...devices.filter((d) => d.id !== patch.id)])
  return next
}

export function forgetDevice(id: string): void {
  commit(devices.filter((d) => d.id !== id))
}

export function findDevice(id: string | undefined): KnownDevice | undefined {
  return id ? devices.find((d) => d.id === id) : undefined
}

/** The nickname if there is one, else the logger's name. */
export function deviceLabel(d: KnownDevice | undefined): string {
  if (!d) return ''
  return d.nick?.trim() || d.name || d.id
}

/** The suggested nickname: the last word of the Wi-Fi name ("AiM-SOLO2-…-Solo 2 ID Tina" → "Tina"). */
export function defaultNick(ssid: string | undefined, name: string): string {
  const source = (ssid ?? name).trim()
  const words = source.split(/\s+/).filter(Boolean)
  const last = words[words.length - 1] ?? ''
  // A name without spaces: take what follows the last dash, if anything meaningful does.
  if (words.length <= 1 && last.includes('-')) {
    const tail = last.split('-').filter(Boolean).pop() ?? ''
    return tail || last
  }
  return last
}
